import {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CompletedOnboardingFeatures } from './types';
import { useAuth } from 'context/auth';
import useGetUserOnboardingFeatures from 'hooks/services/user/useGetUserOnboardingFeatures';
import useGetUserOnboardingIntegrations from 'hooks/services/user/useGetUserOnboardingIntegrations';
import { FeaturesOnboarding } from 'crono-fe-common/types/DTO/featuresOnboarding';
import { OnboardingFeaturesContext } from './contexts';

const OnboardingFeaturesProvider: FC<PropsWithChildren> = ({ children }) => {
  const { user, enabledContextCalls } = useAuth();

  //To prevent the call from being fired multiple times. When the onboardingState === null after checking with the user this is set to false
  const [onboardingCallEnabled, setOnboardingCallEnabled] =
    useState<boolean>(true);

  useEffect(() => {
    //After logout the user is set to null, after this I can call the onboarding API or the Migration
    if (user === null) {
      setOnboardingCallEnabled(true);
    }
  }, [user]);

  const { data: userOnboardingFeatures } = useGetUserOnboardingFeatures(
    enabledContextCalls && onboardingCallEnabled,
  );
  const { data: userOnboardingIntegrations } = useGetUserOnboardingIntegrations(
    enabledContextCalls && onboardingCallEnabled,
  );
  //Calculate the current state of the onBoarding:
  //- undefined: calculating;
  //- null: onBoarding done or user missing;
  //- else: onboarding to finish
  const onboardingFeatures: CompletedOnboardingFeatures | null | undefined =
    useMemo(() => {
      //I make sure that both the API have been called
      if (
        user === undefined ||
        userOnboardingFeatures === undefined ||
        userOnboardingIntegrations === undefined
      )
        return undefined;
      if (!user) return null;
      let state: Partial<CompletedOnboardingFeatures> | null =
        userOnboardingFeatures?.data?.data ?? user.featuresOnboarding ?? null;
      if (
        state &&
        state.createContactOrCompany &&
        state.createStrategy &&
        state.addProspectToStrategy &&
        state.executeTask &&
        state.integrations
      ) {
        //Everything is completed, no need to show the onboarding button
        state = null;
      } else if (state) {
        //For the integrations, false if not done, true if done, null if not to do (not activated on the subscription)
        //Linkedin
        if (user.otherSettings?.hasLinkedin === true) {
          state.linkedinIntegrated =
            userOnboardingIntegrations?.data?.data?.linkedinConnected;
        } else {
          state.linkedinIntegrated = null;
        }

        const stepFlags: (keyof FeaturesOnboarding)[] = [
          'integrations',
          'createContactOrCompany',
          ...(user.otherSettings?.hasSequences
            ? ([
                'createStrategy',
                'addProspectToStrategy',
                'executeTask',
              ] as const)
            : []),
        ];

        // 1. Sum equally % for each completed flag
        let totalPercentage = 0;
        for (const flag of stepFlags) {
          if (state[flag]) {
            totalPercentage += Math.round((1 / stepFlags.length) * 100);
          }
        }

        // 2. Find the first incomplete step
        const firstIncompleteIdx = stepFlags.findIndex(
          (flag) => !state?.[flag],
        );
        const allStepsCompleted = firstIncompleteIdx === -1;
        const currentStep = firstIncompleteIdx + 1; // convert zero-based index to 1-based step

        state.percentage = totalPercentage;
        state.currentStep = currentStep;
        state.onboardingCompleted = allStepsCompleted;
      }
      //Features onboarding done
      if (state === null || state.percentage === 100) {
        setOnboardingCallEnabled(false);
      }
      return state as CompletedOnboardingFeatures;
    }, [user, userOnboardingFeatures, userOnboardingIntegrations]);

  const value = {
    onboardingFeatures,
  };

  return (
    <OnboardingFeaturesContext.Provider value={value}>
      {children}
    </OnboardingFeaturesContext.Provider>
  );
};

export default OnboardingFeaturesProvider;

export function OnboardingFeatures() {
  const context = useContext(OnboardingFeaturesContext);
  if (context === undefined) {
    throw new Error(
      `OnboardingFeatures must be used within a OnboardingFeaturesProvider`,
    );
  }

  return context;
}
